import { useRef } from 'react';

import * as St from './Carousel.styles';

import HorizontalScrollContainer from '~/UI/components/molecules/HorizontalScrollContainer';
import { useCarousel } from '~/core/hooks/useCarousel';
import { ImagePreviewModel } from '~/core/models';
import { formatPriceUnitOrFree } from '~/core/utils';
import ArrowButton from '~atoms/ArrowButton';

type CarouselProps = {
  images: ImagePreviewModel[];
};

const Carousel = ({ images }: CarouselProps) => {
  const thumbnailRefs = useRef<(HTMLImageElement | null)[]>([]);
  const horizontalScrollerRef = useRef<HTMLDivElement>(null);

  const {
    currentIndex,
    prevIndex,
    nextIndex,
    goTo,
    goToNext: _goToNext,
    goToPrev: _goToPrev,
  } = useCarousel(images, 0);

  const scrollTo = (args: {
    direction: 'left' | 'right';
    startOver?: boolean;
  }) => {
    const { direction, startOver = false } = args;
    const el = horizontalScrollerRef.current;

    if (el) {
      const xAxis = direction === 'left' ? -1 : 1;

      if (startOver && direction === 'right') {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else if (startOver && direction === 'left') {
        el.scrollTo({
          left: el.scrollWidth - el.clientWidth,
          behavior: 'smooth',
        });
      } else {
        const scrollAmount = 100 * xAxis; // 100 is the size of the miniatures in the small carousel

        el.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
    }
  };

  const goToNext = () => {
    _goToNext();
    scrollTo({
      direction: 'right',
      startOver: (nextIndex ?? 0) < (currentIndex ?? 0),
    });
  };

  const goToPrev = () => {
    _goToPrev();
    scrollTo({
      direction: 'left',
      startOver: (prevIndex ?? 0) > (currentIndex ?? 0),
    });
  };
  const scrollThumbnailToIndex = (index: number) => {
    const thumbnail = thumbnailRefs.current[index];

    if (!thumbnail) {
      return;
    }

    thumbnail.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  };

  const currentImage = images[currentIndex];
  const altText = 'thumbnail ' + currentIndex;

  const selectImage = (index: number) => {
    scrollThumbnailToIndex(index);

    goTo(index);
  };

  return (
    <>
      <St.Container>
        <St.ImgContainer>
          <St.CarouselImage src={currentImage.image} alt={altText} />
          <ArrowButton
            direction="left"
            handleClick={goToPrev}
            iconStyleProps={{ fontSize: '40rem' }}
            ariaProps={{ 'aria-label': 'Previous Image' }}
          />
          <ArrowButton
            direction="right"
            handleClick={goToNext}
            iconStyleProps={{ fontSize: '40rem' }}
            ariaProps={{ 'aria-label': 'Next Image' }}
          />
        </St.ImgContainer>
        <St.ProductInfoContainer>
          <St.ProductName>{currentImage.productName}</St.ProductName>
          {currentImage.unitPrice || currentImage.unitPrice === 0 ? (
            <St.ProductCost>
              {formatPriceUnitOrFree(
                currentImage.unitPrice,
                currentImage.unitOfMeasure
              )}
            </St.ProductCost>
          ) : null}
        </St.ProductInfoContainer>
      </St.Container>

      <St.ThumbnailsContainer>
        <HorizontalScrollContainer
          ref={horizontalScrollerRef}
          goToNext={goToNext}
          goToPrev={goToPrev}
        >
          {images.map((img, index) => (
            <St.Thumbnail
              key={`${img.image}-${img.productName || index}`}
              src={img.image}
              alt={`Thumbnail ${index}`}
              onClick={() => selectImage(index)}
              data-is-selected={index === currentIndex}
              ref={(el) => {
                thumbnailRefs.current[index] = el;
              }}
            />
          ))}
        </HorizontalScrollContainer>
      </St.ThumbnailsContainer>
    </>
  );
};

export default Carousel;
